// External dependencies
import React, { Component } from 'react';

// Internal dependencies
import {
  getHostsStripeStatus,
  resendOnboarding,
  detachStripeAccount,
  getTransactions,
  getStripeConfig,
  updateStripeConfig,
  getCommissionSettings,
  updateCommissionSettings,
  syncInvoiceStatuses,
} from '../api/stripe.api';
import AdminLayout from './AdminLayout.component';

export default class AdminStripe extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'hosts', // 'hosts', 'transactions', 'api-config', 'commission'
      // Hosts state
      hosts: [],
      loading: true,
      error: null,
      resending: {},
      detaching: {},
      successMessage: null,
      filter: 'all', // 'all', 'with-stripe', 'incomplete', 'complete'
      searchTerm: '',
      // Transactions state
      transactions: [],
      transactionsLoading: false,
      transactionsError: null,
      transactionsPage: 1,
      transactionsLimit: 50,
      transactionsTotal: 0,
      transactionsPages: 1,
      transactionsStatusFilter: 'all',
      transactionsSearch: '',
      // API Config state
      stripeConfig: null,
      configLoading: false,
      configError: null,
      configForm: {
        secretKey: '',
        publishableKey: '',
        webhookSecret: '',
      },
      configSaving: false,
      // Commission state
      commissionSettings: null,
      commissionLoading: false,
      commissionError: null,
      commissionForm: {
        commissionPercent: '',
        commissionFixed: '',
        currency: 'EUR',
      },
      commissionSaving: false,
    };
  }

  async componentDidMount() {
    await this.loadHosts();
    // Load config and commission settings
    await this.loadStripeConfig();
    await this.loadCommissionSettings();
  }

  async loadHosts() {
    try {
      this.setState({ loading: true, error: null, successMessage: null });
      const hosts = await getHostsStripeStatus();
      this.setState({ hosts, loading: false });
    } catch (error) {
      this.setState({
        error: 'Failed to load host Stripe status',
        loading: false,
      });
    }
  }

  async handleDetachAccount(hostId) {
    const host = this.state.hosts.find(h => h._id === hostId);
    const hostName = host?.displayName || host?.email || 'this host';

    if (
      !window.confirm(
        `Detach the Stripe account from ${hostName}?\n\n` +
          'Use this when the account was created in the wrong country. ' +
          'The account stays in Stripe, but HostFolk will forget it and the host ' +
          'will have to connect again and choose their country. ' +
          'Payouts to this host stop until they do.',
      )
    ) {
      return;
    }

    try {
      this.setState(prevState => ({
        detaching: { ...prevState.detaching, [hostId]: true },
        successMessage: null,
      }));

      await detachStripeAccount(hostId);
      this.setState({
        successMessage: `✅ Stripe account detached from ${hostName}. They can now reconnect with the right country.`,
      });

      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 5000);

      await this.loadHosts();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to detach Stripe account. Please try again.';
      alert(`Error: ${errorMessage}`);
    } finally {
      this.setState(prevState => {
        const newDetaching = { ...prevState.detaching };
        delete newDetaching[hostId];
        return { detaching: newDetaching };
      });
    }
  }

  async handleResendOnboarding(hostId) {
    const host = this.state.hosts.find(h => h._id === hostId);
    const hostName = host?.displayName || host?.email || 'this host';

    if (
      !window.confirm(
        `Send onboarding reminder email to ${hostName}? The email will contain a link to complete their Stripe setup.`,
      )
    ) {
      return;
    }

    try {
      this.setState(prevState => ({
        resending: { ...prevState.resending, [hostId]: true },
        successMessage: null,
      }));

      await resendOnboarding(hostId);
      this.setState({
        successMessage: `✅ Onboarding reminder email sent successfully to ${hostName}!`,
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 5000);

      // Refresh the list to get updated status
      await this.loadHosts();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to send onboarding link. Please try again.';
      alert(`Error: ${errorMessage}`);
    } finally {
      this.setState(prevState => {
        const newResending = { ...prevState.resending };
        delete newResending[hostId];
        return { resending: newResending };
      });
    }
  }

  getFilteredHosts() {
    let filtered = [...this.state.hosts];

    // Apply filter
    if (this.state.filter === 'with-stripe') {
      filtered = filtered.filter(h => h.stripeAccountId);
    } else if (this.state.filter === 'incomplete') {
      filtered = filtered.filter(
        h => h.stripeAccountId && !h.stripeOnboardingComplete,
      );
    } else if (this.state.filter === 'complete') {
      filtered = filtered.filter(h => h.stripeOnboardingComplete);
    }

    // Apply search
    if (this.state.searchTerm) {
      const term = this.state.searchTerm.toLowerCase();
      filtered = filtered.filter(
        h =>
          (h.displayName && h.displayName.toLowerCase().includes(term)) ||
          (h.email && h.email.toLowerCase().includes(term)) ||
          (h.stripeAccountId && h.stripeAccountId.toLowerCase().includes(term)),
      );
    }

    return filtered;
  }

  getStatistics() {
    const hosts = this.state.hosts;
    const withStripe = hosts.filter(h => h.stripeAccountId).length;
    const complete = hosts.filter(h => h.stripeOnboardingComplete).length;
    const incomplete = hosts.filter(
      h => h.stripeAccountId && !h.stripeOnboardingComplete,
    ).length;

    return { total: hosts.length, withStripe, complete, incomplete };
  }

  // Transactions methods
  async loadTransactions() {
    try {
      this.setState({ transactionsLoading: true, transactionsError: null });
      const {
        transactionsPage,
        transactionsLimit,
        transactionsStatusFilter,
        transactionsSearch,
      } = this.state;
      const params = {
        page: transactionsPage,
        limit: transactionsLimit,
      };
      if (transactionsStatusFilter !== 'all') {
        params.status = transactionsStatusFilter;
      }
      if (transactionsSearch) {
        params.search = transactionsSearch;
      }
      const data = await getTransactions(params);
      this.setState(prevState => ({
        transactions: data.transactions,
        transactionsTotal: data.pagination.total,
        transactionsPages: data.pagination.pages || 1,
        transactionsPage: data.pagination.page || prevState.transactionsPage,
        transactionsLoading: false,
      }));
    } catch (error) {
      this.setState({
        transactionsError: 'Failed to load transactions',
        transactionsLoading: false,
      });
    }
  }

  handleTransactionsPageChange(newPage) {
    const totalPages =
      this.state.transactionsPages ||
      Math.ceil(this.state.transactionsTotal / this.state.transactionsLimit) ||
      1;
    const safePage = Math.min(Math.max(newPage, 1), Math.max(totalPages, 1));
    if (safePage === this.state.transactionsPage) {
      return;
    }
    this.setState({ transactionsPage: safePage }, () =>
      this.loadTransactions(),
    );
  }

  handleTransactionsLimitChange(newLimit) {
    let shouldReload = false;
    this.setState(
      prevState => {
        const parsedLimit =
          parseInt(newLimit, 10) || prevState.transactionsLimit;
        if (parsedLimit === prevState.transactionsLimit) {
          return null;
        }
        shouldReload = true;
        return {
          transactionsLimit: parsedLimit,
          transactionsPage: 1,
        };
      },
      () => {
        if (shouldReload) {
          this.loadTransactions();
        }
      },
    );
  }

  // API Config methods
  async loadStripeConfig() {
    try {
      this.setState({ configLoading: true, configError: null });
      const config = await getStripeConfig();
      this.setState({
        stripeConfig: config,
        configForm: {
          secretKey: '',
          publishableKey: '',
          webhookSecret: '',
        },
        configLoading: false,
      });
    } catch (error) {
      this.setState({
        configError: 'Failed to load Stripe configuration',
        configLoading: false,
      });
    }
  }

  async handleSaveStripeConfig(e) {
    e.preventDefault();
    try {
      this.setState({ configSaving: true, configError: null });
      await updateStripeConfig(this.state.configForm);
      this.setState({
        successMessage: 'Stripe configuration updated successfully!',
        configSaving: false,
      });
      setTimeout(() => this.setState({ successMessage: null }), 5000);
      await this.loadStripeConfig();
    } catch (error) {
      this.setState({
        configError:
          error.response?.data?.error ||
          'Failed to update Stripe configuration',
        configSaving: false,
      });
    }
  }

  // Commission methods
  async loadCommissionSettings() {
    try {
      this.setState({ commissionLoading: true, commissionError: null });
      const settings = await getCommissionSettings();
      this.setState({
        commissionSettings: settings,
        commissionForm: {
          commissionPercent: settings.defaultCommissionPercent || '',
          commissionFixed: settings.defaultCommissionFixed || '',
          currency: settings.currency || 'EUR',
        },
        commissionLoading: false,
      });
    } catch (error) {
      this.setState({
        commissionError: 'Failed to load commission settings',
        commissionLoading: false,
      });
    }
  }

  async handleSaveCommissionSettings(e) {
    e.preventDefault();
    try {
      this.setState({ commissionSaving: true, commissionError: null });
      await updateCommissionSettings(this.state.commissionForm);
      this.setState({
        successMessage: 'Commission settings updated successfully!',
        commissionSaving: false,
      });
      setTimeout(() => this.setState({ successMessage: null }), 5000);
      await this.loadCommissionSettings();
    } catch (error) {
      this.setState({
        commissionError:
          error.response?.data?.error || 'Failed to update commission settings',
        commissionSaving: false,
      });
    }
  }

  // Sync invoice statuses
  async handleSyncStatuses() {
    if (
      !window.confirm(
        'This will check all pending invoices with Stripe and update their status. Continue?',
      )
    ) {
      return;
    }

    try {
      this.setState({ syncingStatuses: true, syncResult: null });
      const result = await syncInvoiceStatuses();
      this.setState({
        syncResult: result,
        successMessage: `✅ Synced ${result.synced} of ${result.total} invoices!`,
        syncingStatuses: false,
      });
      setTimeout(() => this.setState({ successMessage: null }), 10000);
      // Reload transactions to show updated statuses
      await this.loadTransactions();
    } catch (error) {
      this.setState({
        syncResult: null,
        syncingStatuses: false,
        error: error.response?.data?.error || 'Failed to sync invoice statuses',
      });
    }
  }

  render() {
    const {
      activeTab,
      loading,
      error,
      resending,
      detaching,
      successMessage,
      filter,
      searchTerm,
      transactions,
      transactionsLoading,
      transactionsError,
      transactionsPage,
      transactionsTotal,
      transactionsLimit,
      transactionsPages,
      transactionsStatusFilter,
      transactionsSearch,
      stripeConfig,
      configLoading,
      configError,
      configForm,
      configSaving,
      commissionSettings,
      commissionLoading,
      commissionError,
      commissionForm,
      commissionSaving,
      syncingStatuses,
      syncResult,
    } = this.state;
    const filteredHosts = this.getFilteredHosts();
    const stats = this.getStatistics();
    const totalTransactionPages = Math.max(
      transactionsPages || Math.ceil(transactionsTotal / transactionsLimit),
      1,
    );

    return (
      <AdminLayout>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <h2>Payment Settings</h2>
              <p>
                Manage hosts, transactions, API configuration, and commission
                settings.
              </p>
            </div>
          </div>

          {/* Tabs */}
          <ul
            className="nav nav-tabs"
            style={{ marginTop: '20px', marginBottom: '20px' }}
          >
            <li className={activeTab === 'hosts' ? 'active' : ''}>
              <a
                href="#hosts"
                onClick={e => {
                  e.preventDefault();
                  this.setState({ activeTab: 'hosts' });
                }}
              >
                Hosts
              </a>
            </li>
            <li className={activeTab === 'transactions' ? 'active' : ''}>
              <a
                href="#transactions"
                onClick={e => {
                  e.preventDefault();
                  this.setState({ activeTab: 'transactions' });
                  if (transactions.length === 0) {
                    this.loadTransactions();
                  }
                }}
              >
                Transactions
              </a>
            </li>
            <li className={activeTab === 'api-config' ? 'active' : ''}>
              <a
                href="#api-config"
                onClick={e => {
                  e.preventDefault();
                  this.setState({ activeTab: 'api-config' });
                }}
              >
                API Configuration
              </a>
            </li>
            <li className={activeTab === 'commission' ? 'active' : ''}>
              <a
                href="#commission"
                onClick={e => {
                  e.preventDefault();
                  this.setState({ activeTab: 'commission' });
                }}
              >
                Commission Settings
              </a>
            </li>
          </ul>

          {successMessage && (
            <div className="alert alert-success alert-dismissible">
              <button
                type="button"
                className="close"
                onClick={() => this.setState({ successMessage: null })}
              >
                ×
              </button>
              {successMessage}
            </div>
          )}

          {/* Hosts Tab */}
          {activeTab === 'hosts' && (
            <>
              <div className="row">
                <div className="col-md-8">
                  <h3>Host Management</h3>
                </div>
                <div className="col-md-4 text-right">
                  <button
                    className="btn btn-default"
                    onClick={() => this.loadHosts()}
                    disabled={loading}
                    style={{ marginTop: '20px' }}
                  >
                    {loading ? 'Refreshing...' : '🔄 Refresh'}
                  </button>
                </div>
              </div>

              {loading && <p>Loading...</p>}
              {error && (
                <div className="alert alert-danger">
                  {error}
                  <br />
                  <button
                    className="btn btn-sm btn-default"
                    onClick={() => this.loadHosts()}
                    style={{ marginTop: '10px' }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && (
                <>
                  {successMessage && (
                    <div className="alert alert-success alert-dismissible">
                      <button
                        type="button"
                        className="close"
                        onClick={() => this.setState({ successMessage: null })}
                      >
                        ×
                      </button>
                      {successMessage}
                    </div>
                  )}

                  {/* Statistics Panel */}
                  <div className="row" style={{ marginBottom: '20px' }}>
                    <div className="col-md-3">
                      <div className="panel panel-default">
                        <div className="panel-body text-center">
                          <h3 style={{ margin: '0' }}>{stats.total}</h3>
                          <p style={{ margin: '0', color: '#666' }}>
                            Total Hosts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="panel panel-info">
                        <div className="panel-body text-center">
                          <h3 style={{ margin: '0' }}>{stats.withStripe}</h3>
                          <p style={{ margin: '0', color: '#666' }}>
                            With Stripe Account
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="panel panel-success">
                        <div className="panel-body text-center">
                          <h3 style={{ margin: '0' }}>{stats.complete}</h3>
                          <p style={{ margin: '0', color: '#666' }}>
                            ✅ Complete
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="panel panel-warning">
                        <div className="panel-body text-center">
                          <h3 style={{ margin: '0' }}>{stats.incomplete}</h3>
                          <p style={{ margin: '0', color: '#666' }}>
                            ❌ Incomplete
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="panel panel-default">
                    <div className="panel-body">
                      <div className="row">
                        <div className="col-md-6">
                          <label
                            htmlFor="filter-select"
                            style={{ marginRight: '10px' }}
                          >
                            Filter:
                          </label>
                          <select
                            id="filter-select"
                            className="form-control"
                            style={{ display: 'inline-block', width: 'auto' }}
                            value={filter}
                            onChange={e =>
                              this.setState({ filter: e.target.value })
                            }
                          >
                            <option value="all">All Hosts</option>
                            <option value="with-stripe">
                              With Stripe Account
                            </option>
                            <option value="incomplete">
                              Incomplete Onboarding
                            </option>
                            <option value="complete">
                              Complete Onboarding
                            </option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name, email, or Stripe ID..."
                            value={searchTerm}
                            onChange={e =>
                              this.setState({ searchTerm: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Host Name</th>
                        <th>Email</th>
                        <th>Stripe Account ID</th>
                        <th>Country</th>
                        <th>Onboarding Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHosts.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            {searchTerm || filter !== 'all'
                              ? 'No hosts match your filters'
                              : 'No hosts found'}
                          </td>
                        </tr>
                      ) : (
                        filteredHosts.map(host => (
                          <tr key={host._id}>
                            <td>{host.displayName || 'N/A'}</td>
                            <td>{host.email}</td>
                            <td>
                              <code>
                                {host.stripeAccountId
                                  ? host.stripeAccountId.substring(0, 20) +
                                    '...'
                                  : 'Not created'}
                              </code>
                            </td>
                            <td>
                              {host.stripeAccountId ? (
                                host.stripeConnectCountry || (
                                  <span className="text-muted">unknown</span>
                                )
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {host.stripeAccountId ? (
                                host.stripeOnboardingComplete ? (
                                  <span className="label label-success">
                                    ✅ Complete
                                  </span>
                                ) : (
                                  <span className="label label-warning">
                                    ❌ Incomplete
                                  </span>
                                )
                              ) : (
                                <span className="label label-default">
                                  Not started
                                </span>
                              )}
                            </td>
                            <td>
                              {host.stripeAccountId &&
                                !host.stripeOnboardingComplete && (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() =>
                                      this.handleResendOnboarding(host._id)
                                    }
                                    disabled={resending[host._id]}
                                    title="Send onboarding reminder email to this host"
                                  >
                                    {resending[host._id] ? (
                                      <>
                                        <i
                                          className="fa fa-spinner fa-spin"
                                          style={{ marginRight: '5px' }}
                                        ></i>
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fa fa-envelope"
                                          style={{ marginRight: '5px' }}
                                        ></i>
                                        Resend Onboarding Link
                                      </>
                                    )}
                                  </button>
                                )}
                              {host.stripeOnboardingComplete && (
                                <span className="text-muted">
                                  <i className="fa fa-check-circle text-success"></i>{' '}
                                  Complete
                                </span>
                              )}
                              {host.stripeAccountId && (
                                <button
                                  className="btn btn-sm btn-default"
                                  style={{ marginLeft: '5px' }}
                                  onClick={() =>
                                    this.handleDetachAccount(host._id)
                                  }
                                  disabled={detaching[host._id]}
                                  title="Forget this Stripe account so the host can reconnect in a different country"
                                >
                                  {detaching[host._id] ? (
                                    <>
                                      <i
                                        className="fa fa-spinner fa-spin"
                                        style={{ marginRight: '5px' }}
                                      ></i>
                                      Detaching...
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fa fa-unlink"
                                        style={{ marginRight: '5px' }}
                                      ></i>
                                      Detach account
                                    </>
                                  )}
                                </button>
                              )}
                              {!host.stripeAccountId && (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  <div className="panel panel-info">
                    <div className="panel-heading">
                      <strong>Legend</strong>
                    </div>
                    <div className="panel-body">
                      <ul>
                        <li>
                          <span className="label label-success">
                            ✅ Complete
                          </span>{' '}
                          - Host can receive payments
                        </li>
                        <li>
                          <span className="label label-warning">
                            ❌ Incomplete
                          </span>{' '}
                          - Host needs to complete onboarding
                        </li>
                        <li>
                          <span className="label label-default">
                            Not started
                          </span>{' '}
                          - Host has not started onboarding
                        </li>
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div>
              <div className="row">
                <div className="col-md-8">
                  <h3>Transactions</h3>
                </div>
                <div className="col-md-4 text-right">
                  <button
                    className="btn btn-primary"
                    onClick={() => this.handleSyncStatuses()}
                    disabled={syncingStatuses}
                    style={{ marginTop: '20px' }}
                    title="Sync invoice statuses from Stripe"
                  >
                    {syncingStatuses ? (
                      <>
                        <i
                          className="fa fa-spinner fa-spin"
                          style={{ marginRight: '5px' }}
                        ></i>
                        Syncing...
                      </>
                    ) : (
                      <>
                        <i
                          className="fa fa-refresh"
                          style={{ marginRight: '5px' }}
                        ></i>
                        Sync Statuses from Stripe
                      </>
                    )}
                  </button>
                </div>
              </div>

              {syncResult && (
                <div
                  className={`alert alert-${
                    syncResult.synced > 0 ? 'success' : 'info'
                  }`}
                >
                  <strong>Sync Complete:</strong> Updated {syncResult.synced} of{' '}
                  {syncResult.total} invoices.
                  {syncResult.errors && syncResult.errors.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <strong>Errors:</strong>
                      <ul>
                        {syncResult.errors.map((err, idx) => (
                          <li key={idx}>
                            {err.invoiceNumber}: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div
                className="panel panel-default"
                style={{ marginBottom: '20px' }}
              >
                <div className="panel-body">
                  <div className="row">
                    <div className="col-md-4">
                      <label style={{ marginRight: '10px' }}>Status:</label>
                      <select
                        className="form-control"
                        style={{ display: 'inline-block', width: 'auto' }}
                        value={transactionsStatusFilter}
                        onChange={e => {
                          this.setState(
                            {
                              transactionsStatusFilter: e.target.value,
                              transactionsPage: 1,
                            },
                            () => this.loadTransactions(),
                          );
                        }}
                      >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by invoice number, payment intent, or session ID..."
                        value={transactionsSearch}
                        onChange={e =>
                          this.setState({ transactionsSearch: e.target.value })
                        }
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            this.setState({ transactionsPage: 1 }, () =>
                              this.loadTransactions(),
                            );
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          this.setState({ transactionsPage: 1 }, () =>
                            this.loadTransactions(),
                          );
                        }}
                      >
                        Search
                      </button>
                    </div>
                    <div className="col-md-2">
                      <label style={{ marginRight: '10px' }}>Per page:</label>
                      <select
                        className="form-control"
                        value={transactionsLimit}
                        onChange={e =>
                          this.handleTransactionsLimitChange(e.target.value)
                        }
                      >
                        {[25, 50, 100, 200].map(size => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {transactionsLoading && <p>Loading transactions...</p>}
              {transactionsError && (
                <div className="alert alert-danger">{transactionsError}</div>
              )}

              {!transactionsLoading && !transactionsError && (
                <>
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Host</th>
                        <th>Guest</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Platform Fee</th>
                        <th>Host Payout</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        transactions.map(tx => (
                          <tr key={tx._id}>
                            <td>
                              <code>{tx.invoiceNumber}</code>
                            </td>
                            <td>
                              {tx.host?.displayName || tx.host?.email || 'N/A'}
                            </td>
                            <td>
                              {tx.guest?.displayName ||
                                tx.guest?.email ||
                                'N/A'}
                            </td>
                            <td>
                              {tx.amount?.toFixed(2)} {tx.currency}
                            </td>
                            <td>
                              <span
                                className={`label label-${
                                  tx.status === 'paid'
                                    ? 'success'
                                    : tx.status === 'pending'
                                    ? 'warning'
                                    : 'default'
                                }`}
                              >
                                {tx.status}
                              </span>
                            </td>
                            <td>
                              {tx.platformFeeAmount?.toFixed(2) || '0.00'}{' '}
                              {tx.currency}
                              {tx.platformFeePercent > 0 && (
                                <small> ({tx.platformFeePercent}%)</small>
                              )}
                            </td>
                            <td>
                              {tx.hostPayoutAmount?.toFixed(2) || '0.00'}{' '}
                              {tx.currency}
                            </td>
                            <td>{new Date(tx.created).toLocaleDateString()}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {transactionsTotal > 0 && (
                    <div className="row" style={{ textAlign: 'center' }}>
                      <div className="col-md-6 text-left">
                        <p style={{ marginBottom: '0' }}>
                          Showing{' '}
                          {Math.min(
                            (transactionsPage - 1) * transactionsLimit + 1,
                            transactionsTotal,
                          )}{' '}
                          -
                          {Math.min(
                            transactionsPage * transactionsLimit,
                            transactionsTotal,
                          )}{' '}
                          of {transactionsTotal} transactions
                        </p>
                      </div>
                      <div className="col-md-6 text-right">
                        <div className="btn-group" role="group">
                          <button
                            className="btn btn-default"
                            onClick={() => this.handleTransactionsPageChange(1)}
                            disabled={transactionsPage === 1}
                          >
                            « First
                          </button>
                          <button
                            className="btn btn-default"
                            onClick={() =>
                              this.handleTransactionsPageChange(
                                transactionsPage - 1,
                              )
                            }
                            disabled={transactionsPage === 1}
                          >
                            ‹ Prev
                          </button>
                          <span
                            className="btn btn-default disabled"
                            style={{ pointerEvents: 'none' }}
                          >
                            Page {transactionsPage} of {totalTransactionPages}
                          </span>
                          <button
                            className="btn btn-default"
                            onClick={() =>
                              this.handleTransactionsPageChange(
                                transactionsPage + 1,
                              )
                            }
                            disabled={transactionsPage >= totalTransactionPages}
                          >
                            Next ›
                          </button>
                          <button
                            className="btn btn-default"
                            onClick={() =>
                              this.handleTransactionsPageChange(
                                totalTransactionPages,
                              )
                            }
                            disabled={transactionsPage >= totalTransactionPages}
                          >
                            Last »
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* API Configuration Tab */}
          {activeTab === 'api-config' && (
            <div>
              <h3>Payment Gateway API Configuration</h3>
              {configLoading && <p>Loading configuration...</p>}
              {configError && (
                <div className="alert alert-danger">{configError}</div>
              )}

              {!configLoading && !configError && stripeConfig && (
                <div className="panel panel-default">
                  <div className="panel-heading">
                    <strong>Current Configuration</strong>
                  </div>
                  <div className="panel-body">
                    <p>
                      <strong>Mode:</strong>{' '}
                      {stripeConfig.isTestMode ? (
                        <span className="label label-warning">Test Mode</span>
                      ) : (
                        <span className="label label-success">Live Mode</span>
                      )}
                    </p>
                    <p>
                      <strong>Secret Key:</strong>{' '}
                      {stripeConfig.secretKey || 'Not configured'}
                    </p>
                    <p>
                      <strong>Publishable Key:</strong>{' '}
                      {stripeConfig.publishableKey || 'Not configured'}
                    </p>
                    <p>
                      <strong>Webhook Secret:</strong>{' '}
                      {stripeConfig.webhookSecret || 'Not configured'}
                    </p>
                  </div>
                </div>
              )}

              <div className="panel panel-default">
                <div className="panel-heading">
                  <strong>Update API Keys</strong>
                </div>
                <div className="panel-body">
                  <form onSubmit={e => this.handleSaveStripeConfig(e)}>
                    <div className="form-group">
                      <label>Stripe Secret Key</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="sk_live_... or sk_test_..."
                        value={configForm.secretKey}
                        onChange={e =>
                          this.setState({
                            configForm: {
                              ...configForm,
                              secretKey: e.target.value,
                            },
                          })
                        }
                      />
                      <small className="text-muted">
                        Leave empty to keep current value
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Stripe Publishable Key</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="pk_live_... or pk_test_..."
                        value={configForm.publishableKey}
                        onChange={e =>
                          this.setState({
                            configForm: {
                              ...configForm,
                              publishableKey: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Webhook Secret</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="whsec_..."
                        value={configForm.webhookSecret}
                        onChange={e =>
                          this.setState({
                            configForm: {
                              ...configForm,
                              webhookSecret: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={configSaving}
                    >
                      {configSaving ? 'Saving...' : 'Save Configuration'}
                    </button>
                  </form>
                  <div
                    className="alert alert-warning"
                    style={{ marginTop: '20px' }}
                  >
                    <strong>Note:</strong> API keys are currently stored in
                    environment variables. Changes here may require server
                    restart or environment variable updates.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Commission Settings Tab */}
          {activeTab === 'commission' && (
            <div>
              <h3>Commission Settings</h3>
              {commissionLoading && <p>Loading commission settings...</p>}
              {commissionError && (
                <div className="alert alert-danger">{commissionError}</div>
              )}

              {!commissionLoading && !commissionError && commissionSettings && (
                <div className="panel panel-default">
                  <div className="panel-heading">
                    <strong>Current Settings</strong>
                  </div>
                  <div className="panel-body">
                    <p>
                      <strong>Default Commission:</strong>{' '}
                      {commissionSettings.defaultCommissionPercent}% +{' '}
                      {commissionSettings.defaultCommissionFixed}{' '}
                      {commissionSettings.currency}
                    </p>
                  </div>
                </div>
              )}

              <div className="panel panel-default">
                <div className="panel-heading">
                  <strong>Update Commission Settings</strong>
                </div>
                <div className="panel-body">
                  <form onSubmit={e => this.handleSaveCommissionSettings(e)}>
                    <div className="form-group">
                      <label>Commission Percentage (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="10"
                        min="0"
                        max="100"
                        step="0.01"
                        value={commissionForm.commissionPercent}
                        onChange={e =>
                          this.setState({
                            commissionForm: {
                              ...commissionForm,
                              commissionPercent: e.target.value,
                            },
                          })
                        }
                        required
                      />
                      <small className="text-muted">
                        Platform fee percentage (0-100)
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Fixed Commission Amount</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        value={commissionForm.commissionFixed}
                        onChange={e =>
                          this.setState({
                            commissionForm: {
                              ...commissionForm,
                              commissionFixed: e.target.value,
                            },
                          })
                        }
                        required
                      />
                      <small className="text-muted">
                        Fixed platform fee amount per transaction
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Currency</label>
                      <select
                        className="form-control"
                        value={commissionForm.currency}
                        onChange={e =>
                          this.setState({
                            commissionForm: {
                              ...commissionForm,
                              currency: e.target.value,
                            },
                          })
                        }
                        required
                      >
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                        <option value="AUD">AUD</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={commissionSaving}
                    >
                      {commissionSaving
                        ? 'Saving...'
                        : 'Save Commission Settings'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }
}

AdminStripe.propTypes = {};
